import { Suspense } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, LogIn } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { BrandMark } from '@/components/Brand';
import { Button } from '@/components/ui';
import { InstallButton } from '@/components/Pwa';
import { RouteFallback } from '@/components/common';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ThemeToggle } from '@/lib/theme';
import { cn } from '@/lib/utils';

const publicLinks = [
  { to: '/', label: 'Beranda', end: true },
  { to: '/profil', label: 'Profil' },
  { to: '/pengumuman', label: 'Pengumuman' },
  { to: '/agenda', label: 'Agenda' },
  { to: '/umkm', label: 'UMKM' },
  { to: '/padi', label: 'Pasar Padi' },
  { to: '/pemilih', label: 'Data Pemilih' },
  { to: '/daftar-penjual', label: 'Daftar Penjual' },
  { to: '/layanan', label: 'Layanan' },
  { to: '/infrastruktur', label: 'Infrastruktur' },
  { to: '/kesehatan', label: 'Kesehatan' },
  { to: '/kontak', label: 'Kontak' },
];

export function PublicLayout() {
  const { user } = useAuth();
  const location = useLocation();

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/85 backdrop-blur-md pt-safe">
        <div className="container flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark className="h-9 w-9" />
            <span className="leading-tight">
              <span className="block whitespace-nowrap text-sm font-bold tracking-tight text-foreground">
                TIRTA SMART
              </span>
              <span className="block whitespace-nowrap text-[11px] text-muted-foreground">
                Desa Tirtasari
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-0 xl:flex">
            {publicLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  cn(
                    'whitespace-nowrap rounded-lg px-2 py-2 text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-tirta-50 text-tirta-800 dark:bg-tirta-950/50 dark:text-tirta-200'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-1.5">
            <InstallButton
              showLabel={false}
              variant="ghost"
              size="icon"
              className="hidden sm:inline-flex"
            />
            <ThemeToggle />
            {user ? (
              <Button asChild size="sm">
                <Link to="/app">
                  <LayoutDashboard className="h-4 w-4" /> Dasbor
                </Link>
              </Button>
            ) : (
              <Button asChild size="sm" variant="outline">
                <Link to="/masuk">
                  <LogIn className="h-4 w-4" /> Masuk
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Mobile nav */}
        <div className="border-t border-border xl:hidden">
          <div className="container flex gap-1 overflow-x-auto py-2 scrollbar-thin">
            {publicLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  cn(
                    'whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-tirta-50 text-tirta-800 dark:bg-tirta-950/50 dark:text-tirta-200'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <main key={location.pathname} className="flex-1">
        <Suspense fallback={<RouteFallback />}>
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Suspense>
      </main>

      <footer className="mt-16 border-t border-tirta-900 bg-tirta-950 text-tirta-100">
        <div className="container grid gap-8 py-12 md:grid-cols-4">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2">
              <BrandMark className="h-9 w-9" />
              <span className="font-bold">TIRTA SMART</span>
            </div>
            <p className="mt-3 max-w-xs text-sm text-tirta-200/80">
              Satu Desa, Satu Sistem, Maju Bersama. Portal layanan digital Desa Tirtasari.
            </p>
          </div>
          <div className="text-sm">
            <p className="font-semibold text-white">Pemerintah Desa</p>
            <ul className="mt-3 space-y-1.5 text-tirta-200/80">
              <li>Desa Tirtasari</li>
              <li>Kecamatan Tirtamulya</li>
              <li>Kabupaten Karawang</li>
              <li>Jawa Barat, Indonesia</li>
            </ul>
          </div>
          <div className="text-sm">
            <p className="font-semibold text-white">Informasi</p>
            <ul className="mt-3 space-y-1.5 text-tirta-200/80">
              <li>
                <Link className="transition-colors hover:text-white" to="/profil">
                  Profil Desa
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/pengumuman">
                  Pengumuman
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/agenda">
                  Agenda Kegiatan
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/infrastruktur">
                  Infrastruktur
                </Link>
              </li>
            </ul>
          </div>
          <div className="text-sm">
            <p className="font-semibold text-white">Layanan</p>
            <ul className="mt-3 space-y-1.5 text-tirta-200/80">
              <li>
                <Link className="transition-colors hover:text-white" to="/umkm">
                  Direktori UMKM
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/padi">
                  Pasar Padi
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/pemilih">
                  Data Pemilih (DPS)
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/daftar-penjual">
                  Daftar Penjual
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/layanan">
                  Permohonan Layanan
                </Link>
              </li>
              <li>
                <Link className="transition-colors hover:text-white" to="/kontak">
                  Pengaduan Warga
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-4">
          <p className="container text-center text-xs text-tirta-300/70">
            © {new Date().getFullYear()} Pemerintah Desa Tirtasari · Data pada sistem ini bersifat
            ilustratif.
          </p>
        </div>
      </footer>
    </div>
  );
}
